import React, { useState } from 'react';
import { useOperationsHead } from './hooks/useOperationsHead';

const DiscountProposalManager = () => {
  const { proposeDiscount } = useOperationsHead();
  const [proposal, setProposal] = useState({
    title: '',
    description: '',
    discount_type: 'percentage', // percentage or amount
    discount_value: '',
    applicable_to: 'all', // all, category, specific_products
    category_filter: '',
    product_filter: '',
    start_date: '',
    end_date: '',
    min_purchase_amount: '',
    max_discount_amount: '',
    terms_conditions: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await proposeDiscount.mutateAsync({
        ...proposal,
        status: 'pending_ceo_approval'
      });
      
      // Reset form
      setProposal({
        title: '',
        description: '',
        discount_type: 'percentage',
        discount_value: '',
        applicable_to: 'all',
        category_filter: '',
        product_filter: '',
        start_date: '',
        end_date: '',
        min_purchase_amount: '',
        max_discount_amount: '',
        terms_conditions: ''
      });
    } catch (error) {
      console.error('Error submitting proposal:', error);
    }
  };

  return (
    <div className="p-6 bg-white">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Discount & Offer Proposals</h1>
        <p className="text-gray-600">Propose discounts and offers for CEO approval</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Basic Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Proposal Title *
              </label>
              <input
                type="text"
                required
                value={proposal.title}
                onChange={(e) => setProposal({...proposal, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Diwali Festival Offer"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Type *
              </label>
              <select
                required
                value={proposal.discount_type}
                onChange={(e) => setProposal({...proposal, discount_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="amount">Fixed Amount (₹)</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              required
              value={proposal.description}
              onChange={(e) => setProposal({...proposal, description: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Describe the offer details, target audience, and business rationale..."
            />
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Discount Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Discount Value *
              </label>
              <div className="relative">
                <input
                  type="number"
                  required
                  step="0.01"
                  value={proposal.discount_value}
                  onChange={(e) => setProposal({...proposal, discount_value: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={proposal.discount_type === 'percentage' ? '10.00' : '5000'}
                />
                <span className="absolute right-3 top-2 text-gray-500">
                  {proposal.discount_type === 'percentage' ? '%' : '₹'}
                </span>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Purchase Amount
              </label>
              <input
                type="number"
                value={proposal.min_purchase_amount}
                onChange={(e) => setProposal({...proposal, min_purchase_amount: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="50000"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Discount Amount
              </label>
              <input
                type="number"
                value={proposal.max_discount_amount}
                onChange={(e) => setProposal({...proposal, max_discount_amount: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="10000"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Applicability</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Applicable To *
              </label>
              <select
                required
                value={proposal.applicable_to}
                onChange={(e) => setProposal({...proposal, applicable_to: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Products</option>
                <option value="category">Specific Category</option>
                <option value="specific_products">Specific Products</option>
              </select>
            </div>

            {proposal.applicable_to === 'category' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category Filter
                </label>
                <select
                  value={proposal.category_filter}
                  onChange={(e) => setProposal({...proposal, category_filter: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Category</option>
                  <option value="rings">Rings</option>
                  <option value="necklaces">Necklaces</option>
                  <option value="earrings">Earrings</option>
                  <option value="bracelets">Bracelets</option>
                  <option value="pendants">Pendants</option>
                </select>
              </div>
            )}

            {proposal.applicable_to === 'specific_products' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Product Filter
                </label>
                <input
                  type="text"
                  value={proposal.product_filter}
                  onChange={(e) => setProposal({...proposal, product_filter: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter product IDs or model names (comma separated)"
                />
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Validity Period</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                required
                value={proposal.start_date}
                onChange={(e) => setProposal({...proposal, start_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date *
              </label>
              <input
                type="date"
                required
                value={proposal.end_date}
                onChange={(e) => setProposal({...proposal, end_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-lg">
          <h2 className="text-lg font-semibold mb-4">Terms & Conditions</h2>
          
          <textarea
            value={proposal.terms_conditions}
            onChange={(e) => setProposal({...proposal, terms_conditions: e.target.value})}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter terms and conditions for this offer..."
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Save as Draft
          </button>
          <button
            type="submit"
            disabled={proposeDiscount.isPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {proposeDiscount.isPending ? 'Submitting...' : 'Submit for CEO Approval'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DiscountProposalManager;